import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import Loading from 'flow/components/ui-components/Loading';
import Icon from 'flow/components//ui-components/Icon';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { Sound } from 'flow/types/Sound';

import Button from 'flow/components//ui-components/Button';
import { routes } from 'flow/types/routes';
import { TypedMap } from 'flow/types/TypedMap';
import { homeActionUrlPath } from 'flow/utils';
import Page from 'flow/components/Setting/Page';
import useAppContext from 'flow/hooks/useAppContext';
import { deleteSound, fetchUserSounds } from 'flow/actions/sounds-actions';
import { MiniAudioPlayer } from 'flow/components/AudioPlayers';
import IconButton from 'flow/components/ui-components/IconButton';
import { generatePath } from 'flow/utils/router-utils';
import { useModal } from 'flow/contexts/ModalProvider';
import { flattenSoundUsage, SoundUsageMap } from 'flow/utils/sound-utils';

const SettingsVoicemailSound = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();
    const { openModal } = useModal();

    useEffect(() => {
        dispatch(fetchUserSounds());
    }, [dispatch]);

    const sounds = useAppSelector((state) => state.sounds);
    const userSounds = sounds?.get('sounds');

    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(fetchUserSounds());
        }, 2000);

        return () => clearInterval(interval);
    }, []);

    const showInUseDialog = (usageMap: SoundUsageMap) =>
        openModal('confirmationDialog', {
            title: i18next.t('sound.delete.in_use.title'),
            description: (
                <>
                    <p>{i18next.t('sound.delete.in_use.body')}</p>
                    <ul className="mt-2 list-disc pl-5">
                        {flattenSoundUsage(usageMap, i18next.t).map(
                            (usage, i) => (
                                <li key={`${usage}-${i}`}>{usage}</li>
                            ),
                        )}
                    </ul>
                </>
            ),
            // handleAccept must be set for BaseModal to render the confirm button
            handleAccept: () => {},
            hideCancelButton: true,
        });

    if (!userSounds) {
        return <Loading />;
    }
    const customSounds = userSounds
        ? userSounds.filter((soundDTO) => soundDTO.get('privateSound') === true)
        : null;

    return (
        <Page
            header="settings.voicemail.sound"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.voicemail'], {
                        extensionKey,
                    }),
                )
            }
        >
            <>
                <div className="mb-6 px-2">
                    <Button
                        className="w-full"
                        color="ghost"
                        onClick={() =>
                            openModal('createSound', {
                                extensionKey,
                                isPrivate: true,
                            })
                        }
                    >
                        {i18next.t('Recorder.new.sound')}
                    </Button>
                </div>

                {customSounds?.map((soundDTO: TypedMap<Sound>) => {
                    if (!soundDTO.has('playbackURL')) {
                        return null;
                    }

                    const playbackUrl = homeActionUrlPath(
                        `/${soundDTO.get('playbackURL')}`,
                    );

                    return (
                        <div
                            key={soundDTO.get('key')}
                            className="flex h-13 grow items-center justify-between gap-x-2 rounded-sm p-2 hover:bg-gray-100"
                        >
                            <div className="flex items-center gap-x-2">
                                <div>
                                    {playbackUrl ? (
                                        <MiniAudioPlayer
                                            url={playbackUrl}
                                            uniqueId={`user-sound-key${soundDTO.get(
                                                'key',
                                            )}`}
                                        />
                                    ) : null}
                                </div>

                                <div className="flex items-center text-sm text-gray-800">
                                    {soundDTO.get('name') || ''}
                                </div>
                            </div>

                            <div className="flex items-center gap-x-1">
                                <IconButton
                                    aria-label={i18next.t(
                                        'Tooltip.Nav.Title.Edit',
                                    )}
                                    onClick={() =>
                                        openModal('editSound', {
                                            soundKey: soundDTO.get('key'),
                                        })
                                    }
                                >
                                    <Icon iconKey="edit" />
                                </IconButton>
                                <IconButton
                                    aria-label={i18next.t('Delete.Button.Text')}
                                    onClick={() => {
                                        dispatch(
                                            deleteSound(soundDTO.get('key'), {
                                                onInUse: showInUseDialog,
                                            }),
                                        );
                                    }}
                                >
                                    <Icon iconKey="delete" />
                                </IconButton>
                            </div>
                        </div>
                    );
                })}
            </>
        </Page>
    );
};

export default SettingsVoicemailSound;
